import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import React, { ComponentPropsWithoutRef, ReactNode } from "react";

const buttonVariants = cva(
  `relative inline-flex items-center justify-center px-4 py-2
   border border-transparent rounded-md shadow-xs
   focus:outline-hidden focus:ring-2 focus:ring-offset-2
   disabled:opacity-50`,
  {
    variants: {
      size: {
        small: "text-xs font-medium px-2 py-1",
        medium: "text-sm font-medium",
        large: "text-md font-semibold",
      },
      variant: {
        full: "w-full",
      },
      color: {
        primary:
          "text-neutral-textOnPrimary bg-primary-action hover:bg-primary-actionHover focus:ring-primary-actionHover",
        secondary:
          "text-neutral-text bg-neutral-actionSecondary hover:bg-neutral-actionSecondaryHover focus:ring-neutral-actionSecondaryHover",
        danger:
          "text-neutral-textOnDark bg-danger-action hover:bg-danger-actionHover focus:bg-danger-actionHover",
        neutralDanger:
          "text-danger-text bg-neutral-actionSecondary hover:bg-neutral-actionSecondaryHover focus:ring-neutral-actionSecondaryHover",
      },
    },
  }
);

const iconVariants = cva("inline-flex *:m-auto", {
  variants: {
    position: {
      start: "mr-2",
      end: "ml-2",
    },
  },
});

export type WSButtonProps = ComponentPropsWithoutRef<"button"> &
  VariantProps<typeof buttonVariants> & {
    startIcon?: ReactNode;
    endIcon?: ReactNode;
  };

export const WSButton = ({
  size,
  variant,
  color,
  startIcon,
  endIcon,
  className,
  children,
  ...props
}: WSButtonProps) => {
  return (
    <button className={cn(buttonVariants({ size, variant, color }), className)} {...props}>
      {startIcon && <span className={iconVariants({ position: "start" })}>{startIcon}</span>}
      {children}
      {endIcon && <span className={iconVariants({ position: "end" })}>{endIcon}</span>}
    </button>
  );
};

export default WSButton;
